import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons/faTrashAlt';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import isEqual from 'lodash-es/isEqual';

import WLManageItem from '../WLManageItem';
import RemoveAllWatchlistsModal from '../../../../Modals/RemoveAllWatchlistsModal';
import RemoveSelectedWatchlistsModal from '../../../../Modals/RemoveSelectedWatchlistsModal';
import DropdownSortingFilter from '../DropdownSortingFilter';

import permissionsDecorator from '../../../../../decorators/permissionsDecorator';
import withComponentName from '../../../../../decorators/withComponentName';
import windowWidthDecorator from '../../../../../decorators/windowWidthDecorator';

import sortWatchlistsValues from '../../../../../data/watchlists/sortWatchlistsValues';
import { SILVER } from '../../../../../data/permissions';

import StorageSvc from '../../../../../services/StorageSvc';

import * as watchlistActions from '../../../../../actions/watchlistActions';
import * as searchActions from '../../../../../actions/searchActions';

import Styles from './styles.module.scss';

const WatchlistManageContent = ({ windowWidth, onboarding }) => {
  const cx = classNames.bind(Styles);

  const dispatch = useDispatch();
  const actions = useMemo(() => (
    bindActionCreators({
      ...watchlistActions,
      ...searchActions,
    }, dispatch)
  ), [dispatch]);

  const {
    watchlistsList,
    maxWatchlistSize,
    maxWatchlists,
    accessLevels,
    token,
  } = useSelector(({ watchlistReducer, subscriptions }) => ({
    watchlistsList: watchlistReducer.watchlistsList,
    maxWatchlistSize: subscriptions.permissions.max_watchlist_size,
    maxWatchlists: subscriptions.permissions.max_watchlists,
    accessLevels: subscriptions.permissions.access_levels,
    token: watchlistReducer.userToken,
  }));

  const [openRemoveAllModal, setOpenRemoveAllModal] = useState(false);
  const [openRemoveSelectedModal, setOpenRemoveSelectedModal] = useState(false);

  const [selectedItemsArray, setSelectedItemsArray] = useState([]);

  // default by, after finished need to be changed
  const [sortBy, setSortBy] = useState({ method: '', scending: '' });

  const [watchlistsListNew, setWatchlistsListNew] = useState(watchlistsList);

  const selectedAllInitial = () => {
    if (selectedItemsArray?.length) {
      if (isEqual(selectedItemsArray, watchlistsListNew)) return true;
    }
    return false;
  };

  const [selectedAll, setSelectedAll] = useState({ selected: selectedAllInitial(), initial: true });

  const deleteSelected = (selectedItemsArray?.length > 0)
  && (selectedItemsArray?.length < watchlistsListNew?.length);

  const getDeleteButtonText = () => {
    if (selectedItemsArray?.length && (selectedItemsArray.length !== watchlistsListNew?.length)) {
      return 'Delete Selected';
    }
    return 'Delete All';
  };

  const [deleteButtonText, setDeleteButtonText] = useState(getDeleteButtonText());

  const getState = (setState) => new Promise((resolve) => {
    setState((currentState) => {
      resolve(currentState);
      return currentState;
    });
  });

  const saveState = async (arg) => {
    let updatedState = await getState(setSelectedItemsArray);
    const updatedStateSelectedAll = await getState(setSelectedAll);

    if (arg === 'selectedAll' && updatedStateSelectedAll.selected) {
      updatedState = watchlistsListNew;
    } else if (arg === 'selectedAll' && !updatedStateSelectedAll.selected) {
      updatedState = [];
    }

    StorageSvc.setItem(
      'selectedWatchlistsManage',
      JSON.stringify({
        selectedWatchlists: [...updatedState],
      }),
    );
  };

  const onHandleRemoveModal = () => {
    if (deleteSelected) {
      setOpenRemoveSelectedModal((prevState) => (!prevState));
    } else {
      setOpenRemoveAllModal((prevState) => (!prevState));
    }
  };

  const handleCloseModal = () => {
    setTimeout(() => {
      setOpenRemoveAllModal(false);
      setOpenRemoveSelectedModal(false);
      setSelectedItemsArray([]);
      saveState();
    }, 300);
  };

  const handleSelectedAll = () => {
    setSelectedAll((prevState) => ({ selected: !prevState.selected, initial: false }));
    saveState('selectedAll');
  };

  const handleSelectedWLItem = (item) => {
    if (selectedItemsArray.some((e) => e.id === item.id)) {
      const newArray = selectedItemsArray;
      setSelectedItemsArray(newArray.filter((wl) => wl.id !== item.id));
    } else {
      setSelectedItemsArray((prevState) => ([...prevState, item]));
    }
    saveState();
  };

  const checkSelectedItem = (item) => selectedItemsArray.findIndex((wl) => wl.id === item.id);

  const handleSorting = (obj) => {
    setSortBy(obj);
  };

  const sortingWatchlists = ({ method, scending }) => {
    const alphabetSorting = (a, b) => {
      if (a.name > b.name) {
        return 1;
      }
      if (a.name < b.name) {
        return -1;
      }
      return 0;
    };

    const numbersSorting = (a, b) => {
      if (a.search_queries.length > b.search_queries.length) {
        return 1;
      }
      if (a.search_queries.length < b.search_queries.length) {
        return -1;
      }
      return 0;
    };

    let newArray = [...watchlistsListNew];
    if (method === 'watchlist_name') {
      newArray.sort(alphabetSorting);
      if (scending === 'descending') newArray.reverse();
    } else if (method === 'created') {
      newArray = [...watchlistsList];
      if (scending === 'descending') newArray.reverse();
    } else if (method === 'number_of_topics') {
      newArray.sort(numbersSorting);
      if (scending === 'descending') newArray.reverse();
    }
    setWatchlistsListNew(newArray);
  };

  const getContentSubtitle = () => {
    if (onboarding && accessLevels.includes(SILVER)) {
      return null;
    } if (onboarding && !accessLevels.includes(SILVER)) {
      return (
        <span>{`${watchlistsList?.length} / ${maxWatchlists}`}</span>
      );
    }

    if (accessLevels.includes(SILVER)) {
      return (
        <span>{`${watchlistsList?.length} / 100`}</span>
      );
    }
    return (
      <span>{`${watchlistsList?.length} / ${maxWatchlists}`}</span>
    );
  };

  useEffect(() => {
    const presisted = StorageSvc.getItem('selectedWatchlistsManage');

    // check value from storage
    if (presisted !== null) {
      const { selectedWatchlists } = JSON.parse(presisted);

      const storageSelectedWl = selectedWatchlists
        .filter((wl) => watchlistsListNew.map((wln) => wln.id).indexOf(wl.id) > -1);

      saveState();

      storageSelectedWl.map((item) => handleSelectedWLItem(item));
    }
  }, []);

  useEffect(() => {
    const copyWatchlistsList = watchlistsList;

    if (Array.isArray(copyWatchlistsList)) {
      setWatchlistsListNew(copyWatchlistsList);
    }
  }, [watchlistsList]);

  useEffect(() => {
    if (selectedAll.selected && !selectedAll.initial) {
      setSelectedItemsArray(watchlistsListNew);
    } else if (!selectedAll.initial && !selectedAll.selected
      && (watchlistsListNew?.length === selectedItemsArray?.length)) {
      setSelectedItemsArray([]);
    }
  }, [selectedAll]);

  useEffect(() => {
    sortingWatchlists(sortBy);
  }, [sortBy]);

  useEffect(() => {
    const getUpdateSelectedItemsArray = async () => {
      const res = await getState(setSelectedItemsArray);
      return res;
    };
    getUpdateSelectedItemsArray().then((updSelItemsArray) => {
      if (watchlistsListNew?.length && (
        watchlistsListNew?.length === updSelItemsArray?.length
      )) {
        setSelectedAll({ selected: true, initial: false });
      } else if (watchlistsListNew?.length && !updSelItemsArray?.length) {
        setSelectedAll({ selected: false, initial: false });
      } else if (watchlistsListNew?.length && (
        watchlistsListNew?.length !== updSelItemsArray?.length
      )) {
        setSelectedAll({ selected: false, initial: false });
      }
    });
    setDeleteButtonText(getDeleteButtonText());
  }, [selectedItemsArray]);

  useEffect(() => {
    const selected = selectedItemsArray.filter((wl) => {
      if (watchlistsListNew.findIndex((wl2) => wl2.id === wl.id) > -1) return true;
      return false;
    });
    setSelectedItemsArray(selected);
  }, [watchlistsListNew]);

  return (
    <div className={cx('content', { onboarding })}>
      <h1 className={cx('content__title')}>
        {onboarding ? 'Manage Watchlists' : 'All Watchlists'}
        {getContentSubtitle()}
      </h1>
      <div className={cx('content__filters-panel', 'filters-panel')}>
        <div
          className={cx('filters-panel__item', 'select')}
          onClick={handleSelectedAll}
        >
          <span className={cx('checkbox_box', { checked: selectedAll.selected })}>
            {selectedAll.selected && <FontAwesomeIcon icon={faCheck} />}
          </span>
          <span>Select / Unselect All</span>
        </div>
        <div className={cx('filters-panel__item', 'sorting')}>
          <span className={cx('sorting__title')}>
            Sort by:
          </span>
          <DropdownSortingFilter
            type="sortingWatchlists"
            handleSorting={handleSorting}
            values={sortWatchlistsValues}
            actions={actions}
            token={token}
          />
        </div>
        <div
          onClick={onHandleRemoveModal}
          className={cx('filters-panel__item', 'delete',
            { 'delete-selected': (selectedItemsArray?.length && (selectedItemsArray.length !== watchlistsListNew?.length)) })}
        >
          <span>
            {windowWidth <= 767 && <FontAwesomeIcon icon={faTrashAlt} />}
            {deleteButtonText}
          </span>
        </div>
      </div>
      <div className={cx('content__wrapper')}>
        <div className={cx('content__list')}>
          {watchlistsListNew?.map((item) => (
            <WLManageItem
              handleSelectedWLItem={handleSelectedWLItem}
              item={item}
              checkSelectedItem={checkSelectedItem}
              selectedItemsArray={selectedItemsArray}
              key={item.id}
              watchlistsListNew={watchlistsListNew}
              maxWatchlistSize={maxWatchlistSize}
              onboarding={onboarding}
              manageWlPage
            />
          ))}
        </div>
      </div>
      {deleteSelected ? (
        <RemoveSelectedWatchlistsModal
          openModal={openRemoveSelectedModal}
          handleCloseModal={handleCloseModal}
          selectedWatchlists={selectedItemsArray}
          manageWatchlistsPage
          hasBigScreenDesign
        />
      ) : (
        <RemoveAllWatchlistsModal
          openModal={openRemoveAllModal}
          handleCloseModal={handleCloseModal}
          manageWatchlistsPage
          hasBigScreenDesign
        />
      )}
    </div>
  );
};

export default withComponentName(
  (permissionsDecorator(windowWidthDecorator(WatchlistManageContent))),
);
